import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ExitIcon from "@mui/icons-material/HighlightOff";
import { Chart as ChartJS2 } from "react-chartjs-2";
import "chartjs-adapter-moment";
import ZoomPlugin, { resetZoom } from "chartjs-plugin-zoom";
import "./Chart.css";
import { LanguageContext } from "../context/LanguageContext";
import { useAppTheme } from "../context/ThemeContext";
import { getResponsiveWidgetScale } from "../utils/widgetResponsive";
import {
  getChartDatastreamDomain,
  getNumericRange,
  normalizeChartNumericValue,
  resolveStableChartAxisDomain,
} from "../utils/chartAxisDomain";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineController,
  LineElement,
  Title,
  Legend,
  Tooltip,
  Filler,
} from "chart.js";

ChartJS.register(
  PointElement,
  LinearScale,
  TimeScale,
  CategoryScale,
  LineElement,
  LineController,
  Title,
  Legend,
  Filler,
  Tooltip,
  ZoomPlugin
);

const chartLineGlowPlugin = {
  id: "chartLineGlow",
  beforeDatasetDraw(chart, args, pluginOptions) {
    const dataset = chart.data?.datasets?.[args.index];
    if (!dataset || dataset.type === "bar") return;

    const ctx = chart.ctx;
    ctx.save();
    ctx.shadowColor = dataset.borderColor || pluginOptions?.color || "#35dff1";
    ctx.shadowBlur = pluginOptions?.blur ?? 13;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  },
  afterDatasetDraw(chart) {
    chart.ctx.restore();
  },
};

ChartJS.register(chartLineGlowPlugin);

const getOrCreateTooltip = (chart) => {
  const parent = chart.canvas.parentNode;
  let tooltipEl = parent.querySelector("div.custom-chart-tooltip");

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.className = "custom-chart-tooltip";
    parent.appendChild(tooltipEl);
  }

  return tooltipEl;
};

const placeTooltipInsideChart = (chart, tooltipEl, caretX, caretY) => {
  const PAD = 10;

  const prevOpacity = tooltipEl.style.opacity;
  const prevVis = tooltipEl.style.visibility;
  tooltipEl.style.opacity = "1";
  tooltipEl.style.visibility = "hidden";
  tooltipEl.style.left = "0px";
  tooltipEl.style.top = "0px";

  const w = tooltipEl.offsetWidth || 0;
  const h = tooltipEl.offsetHeight || 0;

  tooltipEl.style.visibility = prevVis;
  tooltipEl.style.opacity = prevOpacity;

  const cw = chart.width;
  const ch = chart.height;

  let left = caretX + PAD;
  let top = caretY - h - PAD;

  if (left + w + PAD > cw) left = caretX - w - PAD;

  if (top < PAD) top = caretY + PAD;

  left = Math.max(PAD, Math.min(left, Math.max(PAD, cw - w - PAD)));
  top = Math.max(PAD, Math.min(top, Math.max(PAD, ch - h - PAD)));

  tooltipEl.style.left = `${left}px`;
  tooltipEl.style.top = `${top}px`;
};

const externalTooltipHandler = (context, datastreams, showUnit, isRtl) => {
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);
  const datastreamList = Array.isArray(datastreams) ? datastreams : [];
  const escapeHtml = (value) =>
    String(value ?? "").replace(/[&<>"']/g, (char) => {
      const entities = {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      };
      return entities[char] || char;
    });

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  tooltipEl.dir = isRtl ? "rtl" : "ltr";

  if (tooltip.body) {
    const titleLines = tooltip.title || [];
    let innerHtml =
      '<div class="chart-tooltip__title">';

    titleLines.forEach((title) => (innerHtml += title));
    innerHtml += '</div><table class="chart-tooltip__table">';

    tooltip.dataPoints.forEach((dataPoint) => {
      const curveInfo = datastreamList.find((ds) => ds.pin === dataPoint.dataset.pin);
      const unit = showUnit !== false ? (curveInfo?.unit || "") : "";
      const rawDecimals = Number(curveInfo?.decimals);
      const decimals = Number.isInteger(rawDecimals)
        ? Math.max(0, Math.min(rawDecimals, 6))
        : 1;
      const rawValue = Number(dataPoint?.parsed?.y);
      const val =
        Number.isFinite(rawValue)
          ? rawValue.toLocaleString(undefined, {
              minimumFractionDigits: decimals,
              maximumFractionDigits: decimals,
            })
          : "N/A";
      const color = dataPoint.dataset.borderColor;
      const curveLabel = dataPoint?.dataset?.label || "Unnamed Curve";
      const safeLabel = escapeHtml(curveLabel);
      const safeValue = escapeHtml(val);
      const safeUnit = escapeHtml(unit);

      innerHtml += `
        <tr>
          <td class="chart-tooltip__leftCell">
            <span class="chart-tooltip__swatch" style="background:${color};"></span>
            <span class="chart-tooltip__label">${safeLabel}</span>
          </td>
          <td class="chart-tooltip__rightCell" style="color:${color};">
            <span class="chart-tooltip__value">${safeValue}</span>
            ${safeUnit ? `<span class="chart-tooltip__unit">${safeUnit}</span>` : ""}
          </td>
        </tr>`;
    });

    innerHtml += "</table>";
    tooltipEl.innerHTML = innerHtml;
  }

  placeTooltipInsideChart(chart, tooltipEl, tooltip.caretX, tooltip.caretY);

  tooltipEl.style.opacity = 1;
};

const ONE_HOUR_SEC = 60 * 60;
const ONE_DAY_SEC = 24 * ONE_HOUR_SEC;

const getTimeUnitConfig = (rangeWindowSec) => {
  const rangeSec = Number(rangeWindowSec);
  if (!Number.isFinite(rangeSec) || rangeSec <= 0) {
    return {
      unit: undefined,
    };
  }

  if (rangeSec <= ONE_DAY_SEC) {
    return {
      unit: "minute",
    };
  }

  return {
    unit: "day",
  };
};

const normalizeChartFontSize = (value, fallback) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return fallback;
  return Math.max(6, Math.min(160, Math.round(numericValue)));
};

const getFontSizeMultiplier = () => {
  if (typeof window === 'undefined') return 1;
  const parsed = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--font-size-multiplier') || '1'
  );
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const scaleChartFontSize = (size, multiplier) =>
  Math.max(6, Math.min(160, Math.round(Number(size || 0) * multiplier)));

const getAutoChartFontSize = (dims, scale, {
  base,
  min,
  max,
}) => {
  const width = Number(dims?.width);
  const height = Number(dims?.height);
  const shortSide = Math.min(
    Number.isFinite(width) && width > 0 ? width : 320,
    Number.isFinite(height) && height > 0 ? height : 220
  );
  const compactReduction = shortSide < 170 ? 2 : shortSide < 240 ? 1 : 0;
  const readableMin = Math.max(10, min - compactReduction);
  return Math.max(readableMin, Math.min(max, Math.round(base * scale)));
};

const formatAxisNumber = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return String(value ?? "");
  const roundedValue = Math.round(numericValue);
  return (Object.is(roundedValue, -0) ? 0 : roundedValue).toLocaleString(undefined, {
    maximumFractionDigits: 0,
    useGrouping: true,
  });
};

const fillForwardValues = (values = []) => {
  let lastValue = null;
  return values.map((value) => {
    const numericValue = normalizeChartNumericValue(value);
    if (numericValue !== null) {
      lastValue = numericValue;
      return numericValue;
    }
    return lastValue;
  });
};

const Chart = ({
  title,
  transparentBackground,
  xData = [],
  min,
  max,
  curves = [],
  isLiveRange = false,
  rangeWindowSec,
  showUnit = true,
  id,
  dims = { width: 850, height: 550 },
  datastreams = [],
  draggable,
  labelFontSize,
  valueFontSize,
}) => {
  const { lang } = useContext(LanguageContext);
  const { mode } = useAppTheme();
  const isRtl = ["ar", "ur"].includes(lang);
  const [zoomIn, setZoomIn] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [cursorState, setCursorState] = useState('idle');
  const chartRef = useRef(null);
  const [zoomWindow, setZoomWindow] = useState(null);
  const zoomWindowRef = useRef(null);
  const zoomContextRef = useRef({
    id,
    isLiveRange,
    rangeWindowSec: Number(rangeWindowSec) || 0,
  });
  const yAxisDomainRef = useRef(null);
  const yAxisDomainSignatureRef = useRef('');

  const toMs = (v) => {
    if (v == null) return null;
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (v instanceof Date) return v.getTime();
    if (typeof v === "string") {
      const t = Date.parse(v);
      return Number.isNaN(t) ? null : t;
    }
    return null;
  };

  const effectiveRangeWindowSec = useMemo(() => {
    const explicit = Number(rangeWindowSec);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;

    const minMs = toMs(min);
    const maxMs = toMs(max);
    if (
      Number.isFinite(minMs) &&
      Number.isFinite(maxMs) &&
      maxMs > minMs
    ) {
      return Math.floor((maxMs - minMs) / 1000);
    }
    return 0;
  }, [rangeWindowSec, min, max]);

  const timeConfig = useMemo(
    () => getTimeUnitConfig(effectiveRangeWindowSec),
    [effectiveRangeWindowSec]
  );

  const yAxisDataRange = useMemo(() => {
    const values = (curves || []).flatMap((curve) => fillForwardValues(curve?.yData || []));
    return getNumericRange(values);
  }, [curves]);

  const yAxisBounds = useMemo(() => {
    const domains = (curves || []).map((curve) => {
      const stream = (datastreams || []).find((ds) => ds?.pin === curve?.pin);
      const dsDomain = getChartDatastreamDomain(stream);
      const curveMin = normalizeChartNumericValue(curve?.min);
      const curveMax = normalizeChartNumericValue(curve?.max);
      return {
        min: dsDomain.min ?? curveMin,
        max: dsDomain.max ?? curveMax,
      };
    });

    const mins = domains.map((domain) => domain.min).filter((value) => value !== null);
    const maxes = domains.map((domain) => domain.max).filter((value) => value !== null);

    return {
      min: mins.length ? Math.min(...mins) : null,
      max: maxes.length ? Math.max(...maxes) : null,
    };
  }, [curves, datastreams]);

  const yAxisDomainSignature = useMemo(() => {
    const pins = (curves || []).map((curve) => String(curve?.pin || '')).join('|');
    const rangePart = isLiveRange
      ? `live|${Number(rangeWindowSec) || 0}`
      : `history|${Number(rangeWindowSec) || 0}|${min || ''}|${max || ''}`;
    return `${id}|${rangePart}|${pins}`;
  }, [curves, id, isLiveRange, max, min, rangeWindowSec]);

  const yAxisDomain = useMemo(() => {
    const shouldReset = yAxisDomainSignatureRef.current !== yAxisDomainSignature;
    return resolveStableChartAxisDomain({
      dataMin: yAxisDataRange.min,
      dataMax: yAxisDataRange.max,
      bounds: yAxisBounds,
      previousDomain: shouldReset ? null : yAxisDomainRef.current,
    });
  }, [yAxisBounds, yAxisDataRange, yAxisDomainSignature]);

  useEffect(() => {
    yAxisDomainSignatureRef.current = yAxisDomainSignature;
    yAxisDomainRef.current = yAxisDomain;
  }, [yAxisDomain, yAxisDomainSignature]);

  const cssVar = (name, fallback) =>
    getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim() || fallback;

  const responsiveTextScale = useMemo(
    () => getResponsiveWidgetScale(dims, { baseWidth: 320, baseHeight: 220, min: 1, max: 2.8 }),
    [dims]
  );
  const fontSizeMultiplier = getFontSizeMultiplier();
  const autoLabelFontSize = getAutoChartFontSize(dims, responsiveTextScale, {
    base: 14,
    min: 13,
    max: 30,
  });
  const autoValueFontSize = getAutoChartFontSize(dims, responsiveTextScale, {
    base: 15,
    min: 14,
    max: 34,
  });
  const autoTitleFontSize = getAutoChartFontSize(dims, responsiveTextScale, {
    base: 21,
    min: 18,
    max: 48,
  });
  const labelFontSizePx = scaleChartFontSize(
    normalizeChartFontSize(labelFontSize, autoLabelFontSize),
    fontSizeMultiplier
  );
  const valueFontSizePx = scaleChartFontSize(
    normalizeChartFontSize(valueFontSize, autoValueFontSize),
    fontSizeMultiplier
  );
  const axisLabelFontSizePx = Math.min(labelFontSizePx, Math.round(12 * fontSizeMultiplier));
  const axisValueFontSizePx = Math.min(valueFontSizePx, Math.round(18 * fontSizeMultiplier));
  const legendFontSizePx = Math.min(labelFontSizePx, Math.round(12 * fontSizeMultiplier));
  const chartTitleFontSizePx = scaleChartFontSize(normalizeChartFontSize(
    labelFontSize,
    autoTitleFontSize
  ), fontSizeMultiplier);

  const syncZoomWindow = useCallback((chart) => {
    const xScale = chart?.scales?.x;
    if (!xScale) return;

    const nextMin = Number(xScale.min);
    const nextMax = Number(xScale.max);

    if (!Number.isFinite(nextMin) || !Number.isFinite(nextMax) || nextMax <= nextMin) {
      zoomWindowRef.current = null;
      setZoomWindow(null);
      setZoomIn(false);
      return;
    }

    const nextWindow = { min: nextMin, max: nextMax };
    const prevWindow = zoomWindowRef.current;
    if (prevWindow?.min === nextWindow.min && prevWindow?.max === nextWindow.max) {
      if (!zoomIn) setZoomIn(true);
      return;
    }

    zoomWindowRef.current = nextWindow;
    setZoomWindow(nextWindow);
    setZoomIn(true);
  }, [zoomIn]);

  const options = useMemo(
    () => {
      return {
        responsive: true,
        animation: false,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        elements: {
          line: {
            borderCapStyle: "round",
            borderJoinStyle: "round",
          },
          point: {
            borderWidth: 0,
          },
        },
        scales: {
          x: {
            type: "time",
            min: zoomWindow?.min ?? min,
            max: zoomWindow?.max ?? max,
            ticks: {
              color: cssVar("--chart-muted", mode === "light" ? "#64748b" : "#64748b"),
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 8,
              font: { size: axisLabelFontSizePx },
            },
            grid: { color: cssVar("--chart-grid-x", mode === "light" ? "rgba(148, 163, 184, 0.24)" : "rgba(51, 65, 85, 0.3)"), drawTicks: false },
            time: {
              unit: timeConfig.unit,
              tooltipFormat: "MMM D, YYYY h:mm:ss a",
              displayFormats: {
                millisecond: "h:mm:ss.SSS a",
                second: "h:mm:ss a",
                minute: "h:mm a",
                hour: "h:mm a",
                day: "MMM D",
                week: "MMM D, YYYY",
                month: "MMM YYYY",
                quarter: "[Q]Q YYYY",
                year: "YYYY",
              },
            },
          },
          y: {
            type: "linear",
            position: isRtl ? "right" : "left",
            beginAtZero: false,
            min: yAxisDomain.min,
            max: yAxisDomain.max,
            grace: yAxisDomain.min === undefined && yAxisDomain.max === undefined ? "10%" : 0,
            ticks: {
              color: cssVar("--chart-legend", mode === "light" ? "#64748b" : "#94a3b8"),
              callback: formatAxisNumber,
              includeBounds: false,
              maxTicksLimit: 6,
              precision: 0,
              font: { size: axisValueFontSizePx },
            },
            grid: { color: cssVar("--chart-grid-y", mode === "light" ? "rgba(148, 163, 184, 0.38)" : "rgba(51, 65, 85, 0.6)") },
          },
        },
        plugins: {
          chartLineGlow: {
            blur: 14,
          },
          legend: {
            position: "top",
            align: "end",
            rtl: isRtl,
            textDirection: isRtl ? "rtl" : "ltr",
            labels: {
              color: cssVar("--chart-legend", mode === "light" ? "#64748b" : "#94a3b8"),
              usePointStyle: true,
              pointStyle: "rectRounded",
              boxWidth: 10,
              boxHeight: 10,
              padding: 12,
              font: { size: legendFontSizePx },
            },
          },
          zoom: {
            pan: {
              enabled: !draggable,
              mode: "x",
              onPanComplete: ({ chart }) => syncZoomWindow(chart),
            },
            zoom: {
              mode: "x",
              wheel: { enabled: true, modifierKey: "ctrl" },
              drag: { enabled: true, modifierKey: "alt" },
              onZoomStart: () => setZoomIn(true),
              onZoomComplete: ({ chart }) => syncZoomWindow(chart),
            },
          },
          tooltip: {
            enabled: false,
            rtl: isRtl,
            textDirection: isRtl ? "rtl" : "ltr",
            external: (context) => externalTooltipHandler(context, datastreams, showUnit, isRtl),
          },
        },
      };
    },
    [min, max, datastreams, draggable, isRtl, axisLabelFontSizePx, axisValueFontSizePx, legendFontSizePx, mode, showUnit, timeConfig, yAxisDomain, zoomWindow, syncZoomWindow]
  );

  const data = useMemo(() => {
    const palette = ["#3b82f6", "#ef4444", "#10b981", "#f59e0b", "#8b5cf6"];
    const referencePalette = ["#8874ff", "#ff837b", "#28c5e2", "#f5b750", "#7dffb2"];
    const datasets = (curves || []).map((curve, idx) => {
      const yValues = fillForwardValues(curve?.yData || []);
      const color = curve.bdColor || referencePalette[idx % referencePalette.length] || palette[idx % palette.length];
      return {
        label: curve.yLabel,
        data: (xData || []).map((x, i) => ({ x, y: yValues[i] ?? null })),
        borderColor: color,
        backgroundColor: color,
        borderWidth: 3,
        pointRadius: 0,
        pointHoverRadius: 7,
        pointHoverBackgroundColor: color,
        pointHoverBorderColor: "rgba(5, 13, 34, 0.96)",
        pointHoverBorderWidth: 2,
        tension: 0.42,
        stepped: false,
        spanGaps: true,
        pin: curve.pin,
      };
    });

    return { datasets };
  }, [curves, xData]);

  const chartInstanceKey = useMemo(() => {
    if (isLiveRange) {
      return `${id}|live|${effectiveRangeWindowSec}`;
    }

    const firstX = xData?.length ? xData[0] : "";
    const lastX = xData?.length ? xData[xData.length - 1] : "";
    return `${id}|history|${effectiveRangeWindowSec}|${firstX}|${lastX}`;
  }, [id, isLiveRange, effectiveRangeWindowSec, xData]);

  useEffect(() => {
    const nextContext = {
      id,
      isLiveRange,
      rangeWindowSec: Number(rangeWindowSec) || 0,
    };
    const prevContext = zoomContextRef.current;

    const shouldResetZoom =
      prevContext.id !== nextContext.id ||
      prevContext.isLiveRange !== nextContext.isLiveRange ||
      prevContext.rangeWindowSec !== nextContext.rangeWindowSec;

    zoomContextRef.current = nextContext;

    if (!shouldResetZoom) return;

    zoomWindowRef.current = null;
    setZoomWindow(null);
    setZoomIn(false);

    if (chartRef.current && (zoomWindowRef.current || zoomIn)) {
      resetZoom(chartRef.current);
    }
  }, [id, isLiveRange, rangeWindowSec, zoomIn]);

  const handleResetZoom = () => {
    if (chartRef.current) {
      resetZoom(chartRef.current);
      zoomWindowRef.current = null;
      setZoomWindow(null);
      setZoomIn(false);
    }
  };

  const handleDragStart = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.x;
    const offsetY = e.clientY - rect.y;

    let dragData = `chart;${id};${offsetX};${offsetY};`;
    dragData += id && id.toString().startsWith("base") ? "add" : "move";
    e.dataTransfer.setData("text/plain", dragData);
  };

  const cursorClass = draggable
    ? (cursorState === 'grabbing' ? 'chart--grabbing' : 'chart--grab')
    : 'chart--default';

  const styleVars = useMemo(() => {
    const w = typeof dims?.width === 'number' ? `${dims.width}px` : (dims?.width || '850px');
    const h = typeof dims?.height === 'number' ? `${dims.height}px` : (dims?.height || '550px');
    const themeBackground = cssVar("--header-background", mode === "light" ? "#F6FFFB" : "#131b2f");

    const bg =
      typeof transparentBackground === 'string' && transparentBackground.trim() !== ''
        ? transparentBackground
        : (transparentBackground
          ? 'transparent'
          : themeBackground);

    return {
      '--chart-width': w,
      '--chart-height': h,
      '--chart-bg-effective': bg,
      '--chart-pointer-events': draggable ? 'none' : 'auto',
      '--chart-title-size': `${Math.min(chartTitleFontSizePx, 18)}px`,
      '--chart-help-size': `${scaleChartFontSize(normalizeChartFontSize(labelFontSize, getAutoChartFontSize(dims, responsiveTextScale, { base: 13, min: 12, max: 22 })), fontSizeMultiplier)}px`,
      '--chart-empty-size': `${scaleChartFontSize(normalizeChartFontSize(valueFontSize, getAutoChartFontSize(dims, responsiveTextScale, { base: 15, min: 13, max: 28 })), fontSizeMultiplier)}px`,
      '--chart-icon-size': `${scaleChartFontSize(getAutoChartFontSize(dims, responsiveTextScale, { base: 24, min: 20, max: 38 }), fontSizeMultiplier)}px`,
    };
  }, [chartTitleFontSizePx, dims, draggable, fontSizeMultiplier, labelFontSize, mode, responsiveTextScale, transparentBackground, valueFontSize]);

  return (
    <div
      className={[
        "widget-container",
        "chart-container",
        transparentBackground === true ? "chart--transparent" : "",
        cursorClass,
      ].filter(Boolean).join(" ")}
      draggable={draggable}
      onDragStart={draggable ? handleDragStart : undefined}
      onMouseDown={() => draggable && setCursorState('grabbing')}
      onMouseUp={() => draggable && setCursorState('grab')}
      onMouseLeave={() => draggable && setCursorState('grab')}
      style={styleVars}
    >
      <div className="chart-header">
        <h3 className="chart-title">{title}</h3>

        <div className="chart-header__actions">
          {!draggable &&
            (zoomIn ? (
              <ExitIcon onClick={handleResetZoom} className="chart-resetZoomIcon" />
            ) : (
              <div className="chart-help">
                <InfoOutlined
                  onMouseEnter={() => setHelpOpen(true)}
                  onMouseLeave={() => setHelpOpen(false)}
                  className="chart-infoIcon"
                />
                <div className={["chart-helpMenu", helpOpen ? "chart-helpMenu--open" : ""].filter(Boolean).join(" ")}>
                  <p className="chart-helpMenu__row">
                    <span className="chart-helpMenu__dot">•</span> CTRL + Wheel to Zoom
                  </p>
                  <p className="chart-helpMenu__row">
                    <span className="chart-helpMenu__dot">•</span> ALT + Drag to Zoom Area
                  </p>
                </div>
              </div>
            ))}
        </div>
      </div>

      <div className="chart-body">
        {(xData.length > 0 || isLiveRange) ? (
          <>
            <ChartJS2 key={chartInstanceKey} ref={chartRef} type="line" data={data} options={options} />
            {xData.length === 0 ? (
              <div className="chart-emptyOverlay">
                Waiting for sensor data...
              </div>
            ) : null}
          </>
        ) : (
          <div className="chart-emptyText">
            Waiting for sensor data...
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(Chart);
